// Small presentational building blocks shared by the Swiper Manager, the
// editor and the Animation Settings placements panel.
import React, { useLayoutEffect, useRef, useState } from "react";
import { FaDesktop, FaMobileAlt, FaTabletAlt } from "react-icons/fa";
import { toImageUrl } from "../../config";
import { elakayAutoText } from "../pageInside/dataElakay";
import DynamicSwiper from "./DynamicSwiper";
import SwiperCanvasSlide from "./SwiperCanvasSlide";
import "./SwiperAdmin.css";

// role="switch" instead of a native checkbox: mdbreact's global CSS makes
// native checkboxes unclickable across front_elakay.
export const Switch = ({ checked, onChange, label, disabled = false, id, describedBy }) => (
  <button
    type="button"
    role="switch"
    id={id}
    aria-checked={checked ? "true" : "false"}
    aria-describedby={describedBy}
    className="dswa-switch"
    disabled={disabled}
    onClick={() => onChange(!checked)}
  >
    <span className="dswa-switch-track" aria-hidden="true" />
    {label && <span>{label}</span>}
  </button>
);

export const Segmented = ({ value, options, onChange, label }) => (
  <div className="dswa-segmented" role="group" aria-label={label}>
    {options.map((option) => (
      <button
        key={option.value}
        type="button"
        aria-pressed={value === option.value ? "true" : "false"}
        onClick={() => onChange(option.value)}
      >
        {option.icon}
        <span>{option.label}</span>
      </button>
    ))}
  </div>
);

export const StatusBadge = ({ status }) => (status === "active"
  ? <span className="dswa-badge dswa-badge-active">{elakayAutoText("h_dsw_status_active", "Active")}</span>
  : <span className="dswa-badge dswa-badge-inactive">{elakayAutoText("h_dsw_status_inactive", "Inactive")}</span>);

// Thumbnail drawn in the swiper's REAL frame ratio. A canvas first slide
// (Swiper Studio, possibly without any image) is drawn by the real renderer
// in miniature -- its sizes are container units, so it scales down exactly.
export const SwiperThumb = ({ url, alt = "", aspectRatio, count, small = false, slide = null }) => {
  const [failed, setFailed] = useState(false);
  const width = Number(aspectRatio?.width) || 16;
  const height = Number(aspectRatio?.height) || 9;
  const canvas = slide?.layout === "canvas";
  return (
    <div className={`dswa-thumb${small ? " dswa-thumb-sm" : ""}${canvas ? " dswa-thumb-canvas" : ""}`} style={{ aspectRatio: `${width} / ${height}` }}>
      {canvas && (
        <div className="dswa-thumb-canvas-frame" aria-hidden="true">
          <SwiperCanvasSlide slide={slide} device="desktop" active={false} animate={false} interactive={false} />
        </div>
      )}
      {!canvas && url && !failed && <img src={toImageUrl(url)} alt={alt} loading="lazy" decoding="async" onError={() => setFailed(true)} />}
      {count ? <span className="dswa-thumb-count">{count}</span> : null}
    </div>
  );
};

export const DEVICE_OPTIONS = () => [
  { value: "desktop", label: elakayAutoText("h_dsw_device_desktop", "Desktop"), icon: <FaDesktop aria-hidden="true" /> },
  { value: "tablet", label: elakayAutoText("h_dsw_device_tablet", "Tablet"), icon: <FaTabletAlt aria-hidden="true" /> },
  { value: "mobile", label: elakayAutoText("h_dsw_device_mobile", "Mobile"), icon: <FaMobileAlt aria-hidden="true" /> },
];

// Width each preview device is rendered at. The page is laid out at that
// real width and scaled down to fit the panel, so "Desktop" really shows a
// 1280 px page (text size, cropping, arrows) and not a squeezed column.
export const PREVIEW_WIDTHS = Object.freeze({ desktop: 1280, tablet: 768, mobile: 375 });
// Screen height each preview simulates, for swipers sized in viewport
// height (Advanced display: "Full screen height", vh units).
export const PREVIEW_HEIGHTS = Object.freeze({ desktop: 800, tablet: 1024, mobile: 740 });
const previewHeightFor = (width) => (width < 576 ? PREVIEW_HEIGHTS.mobile : width < 992 ? PREVIEW_HEIGHTS.tablet : PREVIEW_HEIGHTS.desktop);

const useScaledViewport = (stageRef, viewportRef, width, enabled) => {
  const [box, setBox] = useState({ measured: false, scale: 1, height: 0 });
  useLayoutEffect(() => {
    const stage = stageRef.current;
    const viewport = viewportRef.current;
    if (!enabled || !stage || !viewport || typeof ResizeObserver === "undefined") return undefined;
    const update = () => {
      const style = window.getComputedStyle(stage);
      const available = stage.clientWidth - (parseFloat(style.paddingLeft) || 0) - (parseFloat(style.paddingRight) || 0);
      const scale = available > 0 ? Math.min(1, available / width) : 1;
      const height = viewport.offsetHeight;
      setBox((prev) => (prev.measured && prev.scale === scale && prev.height === height ? prev : { measured: true, scale, height }));
    };
    update();
    const observer = new ResizeObserver(update);
    observer.observe(stage);
    observer.observe(viewport);
    return () => observer.disconnect();
  }, [stageRef, viewportRef, width, enabled]);
  return box;
};

// The editor/manager preview: the REAL <DynamicSwiper> inside a page of the
// device's real width, with placeholder page blocks around it for context.
// width / renderDevice: a custom preview width (Studio "Preview" section)
// and the device layout drawn at that width; hideControls when the caller
// has its own toolbar; replayToken replays the animations; diagnostics
// draws the Advanced display spacing overlay.
export const DevicePreview = ({
  swiper, device, onDeviceChange, showCaption = true, activeIndex, width: customWidth, renderDevice, hideControls = false, replayToken = 0,
  diagnostics = false,
}) => {
  const width = customWidth || PREVIEW_WIDTHS[device] || PREVIEW_WIDTHS.desktop;
  const stageRef = useRef(null);
  const viewportRef = useRef(null);
  const hasSlides = Boolean(swiper?.slides?.length);
  const { measured, scale, height } = useScaledViewport(stageRef, viewportRef, width, hasSlides);
  const deviceLabel = DEVICE_OPTIONS().find((option) => option.value === device)?.label || device;
  const percent = Math.round(scale * 100);
  return (
    <div className="dswa-field">
      {!hideControls && (
        <div className="dswa-label">
          <span>{elakayAutoText("h_dsw_preview", "Preview")}</span>
          <Segmented value={device} onChange={onDeviceChange} options={DEVICE_OPTIONS()} label={elakayAutoText("h_dsw_preview_device", "Preview device")} />
        </div>
      )}
      {/* Buttons inside a preview must never navigate away from the editor. */}
      <div
        ref={stageRef}
        className="dswa-preview-stage"
        data-testid="swiper-preview-stage"
        onClickCapture={(event) => { if (event.target.closest?.("a")) event.preventDefault(); }}
      >
        {hasSlides ? (
          <div
            className={`dswa-preview-device dswa-preview-device-${device}`}
            style={measured ? { width: Math.round(width * scale), height: Math.ceil(height * scale) } : { width: "100%", maxWidth: width }}
          >
            <div
              ref={viewportRef}
              className={`dswa-preview-viewport${measured ? " is-scaled" : ""}`}
              data-preview-width={width}
              data-dsw-viewport=""
              style={{
                ...(measured ? { width, transform: `scale(${scale})` } : {}),
                "--dsw-vh-unit": `${previewHeightFor(width) / 100}px`,
              }}
            >
              <div className="dswa-preview-page-block" aria-hidden="true" />
              <div className="dswa-preview-slot">
                <DynamicSwiper swiper={swiper} device={renderDevice || device} eager activeIndex={activeIndex} replayToken={replayToken}
                  showHidden diagnostics={diagnostics} />
              </div>
              <div className="dswa-preview-page-block" aria-hidden="true" style={{ width: "70%" }} />
            </div>
          </div>
        ) : (
          <p className="dswa-preview-caption">{elakayAutoText("h_dsw_preview_empty", "Add an image to see the preview.")}</p>
        )}
      </div>
      {showCaption && (
        <p className="dswa-preview-caption">
          {measured && percent < 100
            ? elakayAutoText("h_dsw_preview_scaled", "{device} · {width} px wide, shown at {percent}%", { device: deviceLabel, width, percent })
            : elakayAutoText("h_dsw_preview_width", "{device} · {width} px wide", { device: deviceLabel, width })}
          {" · "}
          {elakayAutoText("h_dsw_preview_caption", "Rendered with the same component as the live pages.")}
        </p>
      )}
    </div>
  );
};

const MAIN_PAGE_LABELS = {
  HOME: () => elakayAutoText("h_dsw_page_HOME", "Home"),
  newProducts: () => elakayAutoText("h_dsw_page_newProducts", "New Products"),
  bestSellers: () => elakayAutoText("h_dsw_page_bestSellers", "Best Sellers"),
  dealTodays: () => elakayAutoText("h_dsw_page_dealTodays", "Deal of the Day"),
  productSearch: () => elakayAutoText("h_dsw_page_productSearch", "Search"),
  userView: () => elakayAutoText("h_dsw_page_userView", "Product history"),
  searchHistoryAllCards: () => elakayAutoText("h_dsw_page_searchHistoryAllCards", "Search History"),
  viewingActivityAllCards: () => elakayAutoText("h_dsw_page_viewingActivityAllCards", "Viewing Activity"),
  shopsYouFollowAllCards: () => elakayAutoText("h_dsw_page_shopsYouFollowAllCards", "Shops You Follow"),
  USER_PROFILE: () => elakayAutoText("h_dsw_page_USER_PROFILE", "User Profile"),
  ORDERS: () => elakayAutoText("h_dsw_page_ORDERS", "My Orders"),
  CART: () => elakayAutoText("h_dsw_page_CART", "Shopping Cart"),
};

// Translated name of a registered main page (category pages keep the
// category's own name, sent by the API).
export const mainPageLabel = (pageKey, fallback = pageKey) => (MAIN_PAGE_LABELS[pageKey] ? MAIN_PAGE_LABELS[pageKey]() : fallback);

export const formatDate = (value) => {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
  } catch {
    return "—";
  }
};

// Swiper Access & Limits refusals (server codes), in the user's language.
const capabilityName = (capability) => ({
  controls: elakayAutoText("h_dsw_cap_controls", "navigation, pagination, autoplay and transitions"),
  responsive: elakayAutoText("h_dsw_cap_responsive", "Tablet / Mobile settings"),
  advancedDisplay: elakayAutoText("h_dsw_cap_advanced_display", "the Advanced display (margins, padding, border, corners, sizes)"),
  animations: elakayAutoText("h_dsw_cap_animations", "animations"),
}[capability] || capability);
const ACCESS_CODE_MESSAGES = {
  SWIPER_PUBLISH_DISABLED: () => elakayAutoText("h_dsw_err_publish_disabled", "Your role cannot publish (activate) swipers. Save it as inactive; Developer can activate it."),
  SWIPER_DELETE_DISABLED: () => elakayAutoText("h_dsw_err_delete_disabled", "Your role cannot delete swipers."),
  SWIPER_DUPLICATE_DISABLED: () => elakayAutoText("h_dsw_err_duplicate_disabled", "Your role cannot duplicate swipers."),
  SWIPER_EDIT_DISABLED: () => elakayAutoText("h_dsw_edit_disabled", "Your role cannot edit swipers. Developer can enable it in Animation Settings → Swiper Access & Limits."),
  SWIPER_CREATE_DISABLED: () => elakayAutoText("h_dsw_create_disabled", "Your role cannot create swipers. Developer can enable it in Animation Settings → Swiper Access & Limits."),
  SWIPER_MANAGER_DISABLED: () => elakayAutoText("h_dsw_create_disabled", "Your role cannot create swipers. Developer can enable it in Animation Settings → Swiper Access & Limits."),
  SWIPER_CAPABILITY_DENIED: (error) => elakayAutoText("h_dsw_err_capability_denied", "Your role cannot change {capability}. Undo that change, or ask Developer (Animation Settings → Swiper Access & Limits).", { capability: capabilityName(error.details?.capability) }),
  SWIPER_PLACEMENT_LIMIT: (error) => elakayAutoText("h_dsw_err_placement_limit", "You can use at most {max} swiper(s) on one page.", { max: error.details?.max ?? "?" }),
  SWIPER_QUOTA_REACHED: (error) => (Number.isFinite(error.details?.max)
    ? elakayAutoText("h_dsw_quota_reached", "You have reached your maximum of {max} swipers.", { max: error.details.max })
    : error.message),
};

export const errorMessage = (error, fallback) => {
  if (!error) return fallback;
  if (error.status === 401) return elakayAutoText("h_dsw_err_401", "Your session has expired. Please sign in again.");
  if (ACCESS_CODE_MESSAGES[error.code]) return ACCESS_CODE_MESSAGES[error.code](error) || fallback;
  if (error.status === 403) return elakayAutoText("h_dsw_err_403", "Only accounts with the Developer role can manage dynamic swipers.");
  return error.message || fallback;
};
